<template>
  <div>
    <Head title="Production Queue" />
    <h1 class="mb-8 text-3xl font-bold">Production Queue</h1>
    
    <div class="flex items-center justify-between mb-6">
      <SearchWithDropdownFilter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset">
        <label class="block text-gray-700">Options:</label>
        <select v-model="form.options" class="form-select mt-1 w-full">
          <option :value="null">All SKUs</option>
          <option>To Be Produced > 0</option>
          <option>Total Pending Orders > 0</option>
          <option>Pending Orders and To be Produced > 0</option>
        </select>
      </SearchWithDropdownFilter>
      <search-channel v-model="form.channel" :channels="channels" class="mr-0 w-full max-w-md mr-3" />
      <span class="text-gray-500 text-sm">Last updated: {{ lastUpdate }}</span>
    </div>
    <hr /> <!-- v-if="(queue.data.length > 0 || url())" //-->
    <div class="bg-white rounded-md shadow overflow-x-auto p-5 mb-5">
      <div class="flex items-center justify-between">
        <a
          class="btn-orange px-3 py-2 text-xs cursor-pointer"
          :href="`/download-manufacturing-queue${url()}`" 
          download
          target="__blank"
          title="Download"
        >
          <span>Download</span>
        </a>
      </div>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto mt-5">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Part Number</th>
          <th class="pb-3 pt-3 px-3">Description</th>
          <th class="pb-3 pt-3 px-3">Category</th>
          <th class="pb-3 pt-3 px-3 pr-0 items-end justify-end">Pending Orders</th>
          <th class="pb-3 pt-3 px-3 items-end justify-end">Minimum Inventory</th>
          <th class="pb-3 pt-2 px-3 items-end justify-end">Running Balance</th>
          <th class="pb-3 pt-3 px-3 items-end justify-end">To Be Produced</th>
          <th class="pb-3 pt-3 px-3" colspan="2" />
        </tr>
        <tr v-for="item in queue.data" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <span class="flex items-center px-3 py-2 hover:cursor-pointer" tabindex="-1" @click="openModal(item)">
              {{ item.sku }}
            </span>
          </td>
          <td class="border-t">
            <span class="flex items-center px-3 py-2 hover:cursor-pointer" tabindex="-1" @click="openModal(item)">
              {{ item.description }}
            </span>
          </td>
          <td class="border-t">
            <span class="flex items-center px-3 py-2 hover:cursor-pointer" tabindex="-1" @click="openModal(item)">
              {{ item.category }}
            </span>
          </td>
          <td class="border-t">
            <span class="flex items-end justify-end hover:cursor-pointer" tabindex="-1" @click="openModal(item)">
              {{ item.total_ordered }}
            </span>
          </td>
          <td class="border-t">
            <span class="flex items-end justify-end px-3 py-2 hover:cursor-pointer" tabindex="-1" @click="openModal(item)">
              {{ item.minimum_inventory }}
            </span>
          </td>
          <td class="border-t">
            <span class="flex items-end justify-end px-3 py-2 hover:cursor-pointer" tabindex="-1" @click="openModal(item)">
              {{ item.running_balance }}
            </span>
          </td>
          <td class="border-t">
            <span class="flex items-end justify-end px-3 py-2" tabindex="-1" @click="openModal(item)">
              {{ item.qty_to_produce }}
            </span>
          </td>
          <td class="w-px border-t">
            <span class="flex items-center px-4 hover:cursor-pointer" href="#" tabindex="-1" @click="openModal(item)">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </span>
          </td>
        </tr>
        <tr v-if="queue.data.length === 0">
          <td class="px-2 py-2 border-t" colspan="10">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="queue.links" />
  </div>
  <div v-if="isOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 md:px-6 sm:flex md:flex modal-wrapper w-full">
            <h1 style="font-size:36px;">
              {{ selectedSku.sku }} - {{ selectedSku.description }}
            </h1>
          </div>
          <div v-if="orders.length > 0" class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <table class="w-full whitespace-nowrap">
                <tr class="text-left font-bold">
                  <th class="pb-3 pt-3 px-3">Date</th>
                  <th class="pb-3 pt-3 px-3">Order No</th>
                  <th class="pb-3 pt-3 px-3">Customer</th>
                  <th class="pb-3 pt-3 px-3">Channel</th>
                  <th class="pb-3 pt-3 px-3 flex items-end justify-end">Quantity</th>
                </tr>
                <tr v-for="(item, index) in orders" :key="index">
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-center px-3">{{ item.date }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-left px-3">{{ item.order }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-left px-3">{{ item.customer }}</span>
                  </td>
                  <td class="w-px border-t pb-3 pt-3">
                    <span class="flex items-center px-3">{{ item.channel }}</span>
                  </td>
                  <td class="border-t pb-3 pt-3 flex items-end justify-end">
                    <span class="px-3">{{ item.quantity }}</span>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <div v-else class="p-5">No data found.</div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
  
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchWithDropdownFilter from '@/Shared/SearchWithDropdownFilter'
import SearchChannel from '@/Shared/SearchChannel'

export default {
  components: {
    Head,
    Icon,
    Link,
    Pagination,
    SearchWithDropdownFilter,
    SearchChannel,
  },
  layout: Layout,
  props: {
    filters: Object,
    queue: Object,
    lastUpdate: String,
    channels: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        options: this.filters.options,
        channel: this.filters.channel,
      },
      isOpen: false,
      selectedSku: null,
      orders: [],
    }
  },
  computed: {
    role() {
      return this.$inertia.page.props.auth.user.role
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/manufacturing-queue', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    openModal(sku) {
      this.isOpen = true
      this.orders = sku.orders,
      this.selectedSku = sku
    },
    closeModal() {
      this.isOpen = false
    },
    url() {
      let query = ''
      if (this.form.search) {
        query += `search=${this.form.search}`
      }
      if (this.form.options) {
        if (query) {
          query += '&'
        }
        query += `options=${this.form.options}`
      }
      if (query) {
        query = `?${query}`
      }
      return query
    },
  },
}
</script>
